/**
 * Careers page copy.
 *
 * The client confirmed there are no open roles right now, so this page does not
 * invent any. It presents how the team works and takes open applications to
 * careers@falcon-sol.com. Adding a role later means pushing an entry to
 * `openRoles` — the page switches from the empty state automatically.
 */

export interface Role {
  slug: string;
  title: string;
  location: string;
  type: string;
  summary: string;
  responsibilities: string[];
  requirements: string[];
}

/** Empty by design. Add entries here when hiring opens. */
export const openRoles: Role[] = [];

export const careers = {
  hero: {
    eyebrow: 'Careers',
    headline: 'Build things that stay built',
    subcopy:
      'A small engineering team across Calgary and Lahore, working on software people actually depend on. We hire rarely and carefully. When we do, it is usually someone who introduced themselves before a role existed.',
    /** Baseline strip under the hero. */
    strapline: 'Small team · Real ownership · Work that ships',
  },

  culture: {
    eyebrow: 'How it works here',
    heading: 'What you can expect',
    items: [
      {
        title: 'You own your work',
        body: 'Engineers here scope, build and ship their own features. No one hands you a ticket stripped of context and asks for an estimate.',
      },
      {
        title: 'Direct client contact',
        body: 'You talk to the people using what you build. It makes the work better, and it is the fastest way to learn what actually matters.',
      },
      {
        title: 'Time to do it right',
        body: 'We scope with room for testing and review. Shipping something you are not confident in is not treated as a win here.',
      },
      {
        title: 'Remote, with real overlap',
        body: 'Work from where you are. We keep a deliberate hours overlap between time zones so collaboration does not depend on being awake at odd hours.',
      },
    ],
  },

  /** Client-supplied. Rendered as its own section above the roles list. */
  hybrid: {
    eyebrow: 'How we work',
    heading: 'Hybrid workplace',
    body: 'Work remotely, and come together on-site when it helps: team meetings, collaboration, training.',
    onSite: {
      label: 'Typical time on-site',
      value: 'Flexible',
    },
    benefits: {
      label: 'Featured benefits',
      items: ['Flexible working hours', 'Remote working options', 'Flexible working location'],
    },
  },

  emptyState: {
    heading: 'No advertised roles at the moment',
    body: 'That changes, and it rarely changes with much notice. If you build software carefully and the way we work sounds right, introduce yourself now, because we keep every application on file and go back to them first when a role opens.',
  },

  openApplication: {
    heading: 'Send an open application',
    body: 'Tell us what you work on and link something you have built: a repository, a live project, or anything you can point to.',
  },
} as const;
